// Global Instructions Rule Applied!
//
// Fund-level management-fee calculation basis for one fee window
// (fee__fund_basis): what the fee is charged on, how much of it pays fees,
// and the rate segments the period fee is built from. Body only - callers
// place it inside a FormSection (the workbench's "Fee basis" section) or
// inside an expanded Fee history row.
//
// Fee-paying share is rate-weighted (owner ruling 2026-09-24): a side letter
// at half the headline rate counts half, a 0% commitment counts nothing -
// except on 'Regulatory capital + assumed leverage', where the Assumed
// Leverage share pays the full rate whatever the side letter and so counts
// in full (spec A3.9 addendum part 3).
// Segment rates are the HEADLINE rate and the segment basis is the
// fee-paying basis, so each segment reads fee = basis x rate x fraction.
// A Net Invested Capital basis carries its projection (cfg.fee_bases
// .basis_projection, 2026-10-21): 'book' bills the cost held as the ledger
// carries it, 'cash' takes capitalised interest and the non-cash basis back
// out; the card says which in words under the stats.

import React, { useMemo } from 'react'
import { Alert, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import NumberText from '../../../core/components/NumberText.tsx'
import { FeeBasis, FeeBasisSegment } from '../../../state_manager/data_models/fee_basis.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'

export const formatPercent = (value: number | null | undefined, decimals = 1): string =>
  value == null ? '—' : `${(value * 100).toFixed(decimals)}%`

const segmentLabel = (s: FeeBasisSegment): string =>
  `${dayjs(s.segment_from).format(DATE_FORMAT)} – ${dayjs(s.segment_to).format(DATE_FORMAT)}`

// what a Net Invested Capital projection bills, in words (null = not a
// projected basis)
export const projectionNote = (projection: string | null | undefined): string | null => {
  if (projection === 'book') {
    return 'Book projection: the cost held as the ledger carries it - capitalised interest and non-cash basis (a fair-value step-up, income added to basis) included.'
  }
  if (projection === 'cash') {
    return 'Cash projection: the cash contributions still invested - cost held less capitalised interest and non-cash basis (a fair-value step-up, income added to basis).'
  }
  return null
}

interface FeeBasisCardProps {
  basis: FeeBasis
}

export default function FeeBasisCard({ basis }: FeeBasisCardProps) {
  const segments = useMemo(() => basis.segments ?? [], [basis.segments])
  const mixedBases = useMemo(() => new Set(segments.map((s) => s.basis_label ?? '')).size > 1, [segments])

  if (basis.warning) {
    return <Alert type='warning' showIcon message={basis.warning} />
  }

  const basisAmount = basis.basis_amount ?? 0
  const buckets = basis.buckets
  const share = (amount: number) => (basisAmount > 0 ? (amount / basisAmount) * 100 : 0)
  const isCommitted = (basis.basis_label ?? '') === 'Committed Capital'
  const committedTotal = basis.committed_total ?? 0

  // 'Regulatory capital + assumed leverage': the fund-level base is
  // Regulatory Capital (13 CFR 107.50 / 107.230 via the investors' SBIC
  // investor types) + Assumed Leverage (RC × gearing ratio), as at period end
  const rc = basis.regulatory_capital ?? null
  // Each LP's base is its own Regulatory Capital + gearing × its own
  // Regulatory Capital (spec A3.9 addendum part 5). A snapshot taken before
  // that carries no side_letter_rate_on_leverage key: its base was shared
  // pro rata by commitment.
  const ownRegulatoryCapital = rc?.side_letter_rate_on_leverage != null

  const projection = projectionNote(basis.basis_projection)

  const stats: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Basis', value: basis.basis_label ?? '—' },
    { label: 'Basis amount', value: <NumberText value={basis.basis_amount} /> },
    ...(!isCommitted && committedTotal > 0
      ? [{ label: 'Of committed capital', value: formatPercent(basisAmount / committedTotal) }]
      : []),
    { label: 'Fee-paying share', value: formatPercent(basis.fee_paying_share) },
    { label: 'Fee-paying basis', value: <NumberText value={basis.fee_paying_basis} /> },
    { label: 'Period fee', value: <NumberText value={basis.period_fee} wrap={(v) => <Text strong>{v}</Text>} /> }
  ]

  const columns = [
    {
      title: 'Rate segment',
      key: 'segment',
      render: (_: unknown, s: FeeBasisSegment) => (
        <span>
          {segmentLabel(s)} <Text type='secondary'>({s.days ?? '—'} days)</Text>
          {s.is_late_closer ? (
            <Tag color='blue' className='ml-2'>
              Late closers
            </Tag>
          ) : null}
        </span>
      )
    },
    ...(mixedBases
      ? [{ title: 'Basis', dataIndex: 'basis_label', key: 'basis', render: (v: string | null) => v ?? '—' }]
      : []),
    {
      title: 'Fee-paying basis',
      dataIndex: 'fee_paying_basis',
      key: 'fee_paying_basis',
      align: 'right' as const,
      render: (v: number | null) => <NumberText value={v} />
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      align: 'right' as const,
      render: (v: number | null) => formatPercent(v, 3)
    },
    {
      title: 'Fraction',
      dataIndex: 'year_fraction',
      key: 'year_fraction',
      align: 'right' as const,
      render: (v: number | null) => (v == null ? '—' : v.toFixed(5))
    },
    {
      title: 'Fee',
      dataIndex: 'fee',
      key: 'fee',
      align: 'right' as const,
      render: (v: number | null) => <NumberText value={v} />
    }
  ]

  return (
    <div className='flex flex-col gap-2'>
      <div className='grid grid-cols-2 gap-3 md:grid-cols-6'>
        {stats.map((s) => (
          <div key={s.label}>
            <div className='text-xs text-gray-500'>{s.label}</div>
            <div>{s.value}</div>
          </div>
        ))}
      </div>

      {projection ? (
        <Text type='secondary' style={{ fontSize: 12 }}>
          {projection}
        </Text>
      ) : null}

      {rc ? (
        <div className='rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm'>
          <div className='mb-1 flex items-center justify-between'>
            <Text strong>Regulatory Capital</Text>
            <Text type='secondary' style={{ fontSize: 12 }}>
              as at {rc.as_of ? dayjs(rc.as_of).format(DATE_FORMAT) : '—'} · 13 CFR 107.230
            </Text>
          </div>
          <table className='w-full text-sm'>
            <tbody>
              <tr>
                <td>Institutional commitments (closed)</td>
                <td className='text-right text-gray-500'>{rc.institutional_count ?? 0}</td>
                <td className='text-right'><NumberText value={rc.institutional_capital} /></td>
              </tr>
              <tr>
                <td>
                  Non-institutional paid-in capital{' '}
                  <Text type='secondary'>(of <NumberText value={rc.non_institutional_committed} /> committed)</Text>
                </td>
                <td className='text-right text-gray-500'>{rc.non_institutional_count ?? 0}</td>
                <td className='text-right'><NumberText value={rc.non_institutional_paid_in} /></td>
              </tr>
              <tr>
                <td>State or local government commitments</td>
                <td className='text-right text-gray-500'>{rc.government_count ?? 0}</td>
                <td className='text-right'><NumberText value={rc.government_committed} /></td>
              </tr>
              {rc.government_cap_applied ? (
                <tr>
                  <td className='pl-4 text-amber-700'>33% aggregate cap applied — counted</td>
                  <td />
                  <td className='text-right text-amber-700'><NumberText value={rc.government_counted} /></td>
                </tr>
              ) : null}
              <tr className='border-t border-gray-200'>
                <td><Text strong>Regulatory Capital</Text></td>
                <td />
                <td className='text-right'><NumberText value={rc.regulatory_capital} wrap={(v) => <Text strong>{v}</Text>} /></td>
              </tr>
              <tr>
                <td>
                  Assumed Leverage{' '}
                  <Text type='secondary'>
                    (gearing {rc.gearing_ratio != null ? `${rc.gearing_ratio} ×` : 'not set'})
                  </Text>
                </td>
                <td />
                <td className='text-right'><NumberText value={rc.assumed_leverage} /></td>
              </tr>
              <tr className='border-t border-gray-200'>
                <td><Text strong>Fee base</Text></td>
                <td />
                <td className='text-right'>
                  <NumberText value={(rc.regulatory_capital ?? 0) + (rc.assumed_leverage ?? 0)} wrap={(v) => <Text strong>{v}</Text>} />
                </td>
              </tr>
            </tbody>
          </table>
          {ownRegulatoryCapital ? (
            <Text type='secondary' style={{ fontSize: 12 }}>
              Each LP's fee base is its own Regulatory Capital plus {rc.gearing_ratio ?? 0} × its own Regulatory
              Capital.{' '}
              {rc.side_letter_rate_on_leverage
                ? 'Side-letter rates apply to the whole base.'
                : 'The fee on Assumed Leverage is charged at the full rate whatever the side letter.'}{' '}
              The § 11.9(b) credit to the primary fund uses the fee on Regulatory Capital only.
            </Text>
          ) : (
            <Text type='secondary' style={{ fontSize: 12 }}>
              Allocated to LPs pro-rata by commitment share of the closed pool (<NumberText value={rc.pool} />). The §
              11.9(b) credit to the primary fund uses the Regulatory Capital share only.
            </Text>
          )}
          {(rc.uncategorised_count ?? 0) > 0 ? (
            <Alert
              type='warning'
              showIcon
              className='mt-2'
              message={`${rc.uncategorised_count} investor(s) have no SBIC investor type; counted as non-institutional (paid-in only).`}
            />
          ) : null}
        </div>
      ) : null}

      {buckets && basisAmount > 0 ? (
        <div>
          <div className='flex h-2 w-full overflow-hidden rounded'>
            <div className='bg-blue-500' style={{ width: `${share(buckets.full.amount)}%` }} />
            <div className='bg-blue-200' style={{ width: `${share(buckets.reduced.amount)}%` }} />
            <div className='bg-gray-300' style={{ width: `${share(buckets.non_fee_paying.amount)}%` }} />
          </div>
          <div className='mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500'>
            <span>
              <span className='mr-1 inline-block h-2 w-2 rounded-sm bg-blue-500' />
              Full rate <NumberText value={buckets.full.amount} /> ({buckets.full.count})
            </span>
            {buckets.reduced.count > 0 ? (
              <span>
                <span className='mr-1 inline-block h-2 w-2 rounded-sm bg-blue-200' />
                Side-letter rate <NumberText value={buckets.reduced.amount} /> ({buckets.reduced.count}), counts as{' '}
                <NumberText value={buckets.reduced.fee_paying_amount} />
              </span>
            ) : null}
            {buckets.non_fee_paying.count > 0 ? (
              <span>
                <span className='mr-1 inline-block h-2 w-2 rounded-sm bg-gray-300' />
                Non-fee-paying <NumberText value={buckets.non_fee_paying.amount} /> ({buckets.non_fee_paying.count})
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <Table
        size='small'
        pagination={false}
        rowKey={(s) => `${s.segment_from}-${s.segment_to}-${s.rate}-${s.basis_label}`}
        dataSource={segments}
        columns={columns}
        locale={{ emptyText: 'No fees accrue in this period' }}
        summary={() =>
          segments.length > 1 ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={columns.length - 1}>
                <Text strong>Period fee</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align='right'>
                <NumberText value={basis.period_fee} wrap={(v) => <Text strong>{v}</Text>} />
              </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        }
      />

      <Text type='secondary' style={{ fontSize: 12 }}>
        {[basis.day_count_label, basis.frequency_label].filter(Boolean).join(' · ')}
        {basis.day_count_label || basis.frequency_label ? ' · ' : ''}
        fee = fee-paying basis × rate × fraction. Rates are headline rates; side letters are reflected in the fee-paying
        basis.
        {basis.has_late_closers ? ' Late closers accrue from their closing date.' : ''}
      </Text>
    </div>
  )
}
